import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineClipboardDocumentCheck,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

// Synchronous cache loading function
const getCachedPmComplianceData = () => {
  try {
    const cachedCompliance = sessionStorage.getItem('pm_compliance_data');
    
    return {
      complianceItems: cachedCompliance ? JSON.parse(cachedCompliance) : [],
      hasAll: !!cachedCompliance
    };
  } catch (e) {
    console.error('Error parsing cached PM Compliance data:', e);
    return { complianceItems: [], hasAll: false };
  }
};

const PmCompliancePage = () => {
  // Initialize state with cached data if available (synchronous)
  const cachedInitialData = getCachedPmComplianceData();
  const [complianceItems, setComplianceItems] = useState(cachedInitialData.complianceItems);
  const [loading, setLoading] = useState(!cachedInitialData.hasAll);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    kpiName: '',
    targetValue: '',
    periodType: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      // Load fresh data in background if we have cached data, otherwise show loading
      const hasCachedData = cachedInitialData.hasAll;
      loadComplianceItems(!hasCachedData);
    } else {
      setError('Please login to view PM compliance');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadComplianceItems = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      const data = await masterDataService.getPmCompliance(tenantId);
      setComplianceItems(data || []);
      
      // Cache the data
      sessionStorage.setItem('pm_compliance_data', JSON.stringify(data || []));
    } catch (err) {
      setError('Failed to load PM compliance. Please try again.');
      console.error('Error loading PM compliance:', err);
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasAll) {
        setComplianceItems([]);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredItems = complianceItems.filter((item) =>
    item.kpiName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.periodType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      kpiName: '',
      targetValue: '',
      periodType: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      kpiName: item.kpiName || '',
      targetValue: item.targetValue?.toString() || '',
      periodType: item.periodType || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      // Note: deleteFn is null in config, so we'll skip delete for now
      setError('Delete functionality is not available for PM compliance.');
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete PM compliance. Please try again.');
      console.error('Error deleting PM compliance:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.kpiName.trim()) {
      errors.kpiName = 'KPI name is required';
    }
    if (!formData.targetValue || isNaN(parseFloat(formData.targetValue))) {
      errors.targetValue = 'Target value is required and must be a number';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const complianceData = {
        tenantId: tenantId,
        kpiName: formData.kpiName.trim(),
        targetValue: parseFloat(formData.targetValue),
        periodType: formData.periodType.trim() || null,
      };

      if (editingItem) {
        complianceData.complianceId = editingItem.complianceId;
        await masterDataService.updatePmCompliance(editingItem.complianceId, complianceData);
      } else {
        await masterDataService.createPmCompliance(complianceData);
      }

      await loadComplianceItems();
      setShowModal(false);
      setFormData({ kpiName: '', targetValue: '', periodType: '' });
      setEditingItem(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save PM compliance. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ kpiName: '', targetValue: '', periodType: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading PM compliance..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>PM Compliance</h1>
          <p>Manage PM compliance KPIs for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Compliance
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search compliance..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
          </div>
        </div>

        {filteredItems.length === 0 ? (
          <div className="empty-state">
            <HiOutlineClipboardDocumentCheck className="empty-icon" />
            <h3>No compliance items found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first PM compliance item'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Compliance
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>KPI Name</th>
                <th>Target Value</th>
                <th>Period Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.complianceId}>
                  <td>{item.complianceId}</td>
                  <td className="name-cell">{item.kpiName}</td>
                  <td>{item.targetValue}</td>
                  <td>{item.periodType || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(item)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingItem ? 'Edit PM Compliance' : 'Add New PM Compliance'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="pm-compliance-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingItem
                  ? 'Updating...'
                  : 'Creating...'
                : editingItem
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="pm-compliance-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="kpiName">
                KPI Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="kpiName"
                value={formData.kpiName}
                onChange={(e) =>
                  setFormData({ ...formData, kpiName: e.target.value })
                }
                className={formErrors.kpiName ? 'error' : ''}
                placeholder="Enter KPI name"
              />
              {formErrors.kpiName && (
                <span className="field-error">{formErrors.kpiName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="targetValue">
                Target Value <span className="required">*</span>
              </label>
              <input
                type="number"
                id="targetValue"
                value={formData.targetValue}
                onChange={(e) =>
                  setFormData({ ...formData, targetValue: e.target.value })
                }
                className={formErrors.targetValue ? 'error' : ''}
                placeholder="Enter target value"
                step="0.01"
              />
              {formErrors.targetValue && (
                <span className="field-error">{formErrors.targetValue}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="periodType">Period Type</label>
              <input
                type="text"
                id="periodType"
                value={formData.periodType}
                onChange={(e) =>
                  setFormData({ ...formData, periodType: e.target.value })
                }
                placeholder="e.g., Monthly, Quarterly (optional)"
              />
            </div>
          </form>
        </div>
      </SharedModal>
    </div>
  );
};

export default PmCompliancePage;
